"use client";

import { CalculatorSection } from "@/components/calculators/shared/calculator-workbench";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { InlineCombobox } from "@/components/ui/inline-combobox";
import { ToggleGroup, ToggleGroupItem } from "@/components/ui/toggle-group";
import type { GeometryCategory } from "@/lib/calculators/truck-loading-merged/types";
import { isOptionValue } from "@shared/validation/is-option-value";
import type { ProductTypeConfig } from "@/lib/calculators/truck-loading-merged/product-presets";
import { DimensionGrid, type DimensionGridProps } from "./dimension-grid";

interface DimensionInputsProps extends Omit<DimensionGridProps, "showPalletized"> {
  config: ProductTypeConfig;
  presetId: string;
  geometryCategory: GeometryCategory;
  stackable: boolean;
  onPresetChange: (presetId: string) => void;
  onStackableChange: (value: boolean) => void;
  onGeometryCategoryChange: (category: GeometryCategory) => void;
}

function PresetField({
  config,
  presetId,
  onPresetChange,
}: Pick<DimensionInputsProps, "config" | "presetId" | "onPresetChange">) {
  return (
    <div>
      <Label htmlFor="item-preset">Preset</Label>
      <div className="mt-2">
        <InlineCombobox
          id="item-preset"
          value={presetId}
          options={config.presets.map((preset) => ({
            value: preset.id,
            label: preset.label,
          }))}
          onSelect={(value) => value && onPresetChange(value)}
          placeholder="Select…"
          searchPlaceholder="Search presets"
        />
      </div>
    </div>
  );
}

function GeometryField({
  singularLabel,
  options,
  geometryCategory,
  onGeometryCategoryChange,
}: Pick<DimensionInputsProps, "geometryCategory" | "onGeometryCategoryChange"> & {
  singularLabel: string;
  options: NonNullable<ProductTypeConfig["geometryOptions"]>;
}) {
  return (
    <div>
      <Label id="item-geometry-label">{singularLabel} type</Label>
      <ToggleGroup
        aria-labelledby="item-geometry-label"
        type="single"
        value={geometryCategory}
        onValueChange={(value) => {
          if (isOptionValue(options, value)) onGeometryCategoryChange(value);
        }}
        variant="outline"
        size="sm"
        className="mt-2 justify-start"
      >
        {options.map((option) => (
          <ToggleGroupItem key={option.value} value={option.value} className="flex-1">
            {option.label}
          </ToggleGroupItem>
        ))}
      </ToggleGroup>
    </div>
  );
}

function StackableField({
  pluralLabel,
  stackable,
  onStackableChange,
}: {
  pluralLabel: string;
  stackable: boolean;
  onStackableChange: (value: boolean) => void;
}) {
  return (
    <div className="flex items-center gap-2">
      <Checkbox
        id="item-stackable"
        checked={stackable}
        onCheckedChange={(checked) => onStackableChange(checked === true)}
      />
      <Label htmlFor="item-stackable" weight="normal">
        Stackable: load {pluralLabel.toLowerCase()} on top of each other
      </Label>
    </div>
  );
}

export function DimensionInputs({
  config,
  presetId,
  geometryCategory,
  stackable,
  onPresetChange,
  onStackableChange,
  onGeometryCategoryChange,
  ...gridProps
}: DimensionInputsProps) {
  return (
    <CalculatorSection title={`${config.singularLabel} dimensions`}>
      <div className="space-y-4">
        <PresetField config={config} presetId={presetId} onPresetChange={onPresetChange} />
        {config.geometryOptions && (
          <GeometryField
            singularLabel={config.singularLabel}
            options={config.geometryOptions}
            geometryCategory={geometryCategory}
            onGeometryCategoryChange={onGeometryCategoryChange}
          />
        )}
        <DimensionGrid {...gridProps} showPalletized={config.supportsPalletized} />
        <StackableField
          pluralLabel={config.pluralLabel}
          stackable={stackable}
          onStackableChange={onStackableChange}
        />
      </div>
    </CalculatorSection>
  );
}
